/**
 * VirtualJoystick - Touch-basierter Joystick für Mobile Steuerung
 * Unterstützt Multi-Touch (10 Finger gleichzeitig im Browser)
 */

import React, { useRef, useEffect, useState } from 'react';
import { Box } from '@mui/material';

export interface JoystickState {
  x: number; // -1 bis 1 (links/rechts)
  y: number; // -1 bis 1 (oben/unten)
  active: boolean;
}

interface VirtualJoystickProps {
  size?: number;
  onChange: (state: JoystickState) => void;
  position?: 'left' | 'right';
  color?: string;
}

export const VirtualJoystick: React.FC<VirtualJoystickProps> = ({
  size = 120,
  onChange,
  position = 'left',
  color = '#00aaff'
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [stick, setStick] = useState({ x: 0, y: 0 });
  const [active, setActive] = useState(false);
  const [touchId, setTouchId] = useState<number | null>(null);
  
  const maxDistance = size / 2 - 15; // Maximale Stick-Distanz

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleTouchStart = (e: TouchEvent) => {
      // Multi-Touch: Finde Touch innerhalb des Joystick-Bereichs
      const rect = container.getBoundingClientRect();
      
      for (let i = 0; i < e.touches.length; i++) {
        const touch = e.touches[i];
        const x = touch.clientX - rect.left;
        const y = touch.clientY - rect.top;
        
        // Prüfe ob Touch innerhalb des Joysticks ist
        if (x >= 0 && x <= size && y >= 0 && y <= size) {
          e.preventDefault();
          setTouchId(touch.identifier);
          setActive(true);
          
          const centerX = size / 2;
          const centerY = size / 2;
          const dx = x - centerX;
          const dy = y - centerY;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance > maxDistance) {
            const angle = Math.atan2(dy, dx);
            const clampedX = Math.cos(angle) * maxDistance;
            const clampedY = Math.sin(angle) * maxDistance;
            setStick({ x: clampedX, y: clampedY });
            onChange({
              x: clampedX / maxDistance,
              y: clampedY / maxDistance,
              active: true
            });
          } else {
            setStick({ x: dx, y: dy });
            onChange({
              x: dx / maxDistance,
              y: dy / maxDistance,
              active: true
            });
          }
          break; // Nur den ersten passenden Touch nehmen
        }
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (touchId === null) return;
      
      // Multi-Touch: Finde den richtigen Touch anhand der ID
      for (let i = 0; i < e.touches.length; i++) {
        const touch = e.touches[i];
        if (touch.identifier !== touchId) continue;
        
        e.preventDefault();
        const rect = container.getBoundingClientRect();
        const x = touch.clientX - rect.left;
        const y = touch.clientY - rect.top;
        
        const centerX = size / 2;
        const centerY = size / 2;
        const dx = x - centerX;
        const dy = y - centerY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance > maxDistance) {
          const angle = Math.atan2(dy, dx);
          const clampedX = Math.cos(angle) * maxDistance;
          const clampedY = Math.sin(angle) * maxDistance;
          setStick({ x: clampedX, y: clampedY });
          onChange({
            x: clampedX / maxDistance,
            y: clampedY / maxDistance,
            active: true
          });
        } else {
          setStick({ x: dx, y: dy });
          onChange({
            x: dx / maxDistance,
            y: dy / maxDistance,
            active: true
          });
        }
        break;
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      // Multi-Touch: Prüfe ob unser Touch beendet wurde
      let found = false;
      for (let i = 0; i < e.touches.length; i++) {
        if (e.touches[i].identifier === touchId) {
          found = true;
          break;
        }
      }
      
      if (!found && touchId !== null) {
        e.preventDefault();
        setStick({ x: 0, y: 0 });
        setActive(false);
        setTouchId(null);
        onChange({ x: 0, y: 0, active: false });
      }
    };

    // Touch Events mit { passive: false } für preventDefault
    container.addEventListener('touchstart', handleTouchStart, { passive: false });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: false });
    container.addEventListener('touchcancel', handleTouchEnd, { passive: false });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [size, maxDistance, onChange, touchId]);

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'fixed',
        bottom: 20,
        [position]: 20,
        width: size,
        height: size,
        borderRadius: '50%',
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
        border: `2px solid ${color}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        touchAction: 'none',
        userSelect: 'none',
        zIndex: 9999,
        transition: 'opacity 0.2s',
        opacity: active ? 0.9 : 0.6
      }}
    >
      {/* Stick */}
      <Box
        sx={{
          position: 'absolute',
          width: 30,
          height: 30,
          borderRadius: '50%',
          backgroundColor: color,
          boxShadow: active ? `0 0 20px ${color}` : 'none',
          transform: `translate(${stick.x}px, ${stick.y}px)`,
          transition: active ? 'none' : 'transform 0.15s ease-out',
          pointerEvents: 'none'
        }}
      />
      
      {/* Center Dot */}
      <Box
        sx={{
          width: 4,
          height: 4,
          borderRadius: '50%',
          backgroundColor: color,
          opacity: 0.5,
          pointerEvents: 'none'
        }}
      />
    </Box>
  );
};
